import { IconButton } from "@controls";
import { BarsRegular, ChevronsLeftSolid, ChevronsRightSolid } from "@icons";
import * as Dialog from "@radix-ui/react-dialog";
import * as RadixNavigationMenu from "@radix-ui/react-navigation-menu";
import classNames from "classnames";
import { Outlet } from "react-router-dom";

import { useAppContext } from "../../contexts/app-context/hook";
import { useLocalstorage } from "../../hooks/helperHooks";

export interface GeneralLayoutProps {
  menuList: {
    key: string;
    component: React.FC<{
      minimized: boolean;
      setMobileMenuOpen: (open: boolean) => void;
    }>;
  }[];
  extraMenu: {
    key: string;
    component: React.FC<{
      minimized: boolean;
      setMobileMenuOpen: (open: boolean) => void;
    }>;
  }[];
}

export function GeneralLayout({
  menuList,
  extraMenu = [],
}: GeneralLayoutProps) {
  const { mobileMenuOpen, setMobileMenuOpen } = useAppContext();
  const [storedMinimizedValue, setStoredMinimizedValue] = useLocalstorage(
    "MENU-MINIMIZED",
    "false",
  );
  const minimized = storedMinimizedValue === "true";

  return (
    <>
      <div
        className={classNames(
          "w-full h-full lg:p-4",
          "flex flex-row bg-gray-100",
        )}
      >
        <div
          className={classNames(
            "hidden md:flex flex-col justify-between m-2 lg:m-0",
            {
              "w-14": minimized,
              "min-w-[260px] max-w-[260px]": !minimized,
            },
          )}
        >
          <div className="flex flex-col justify-between">
            <div className="rounded flex-grow w-full">
              <RadixNavigationMenu.Root>
                <RadixNavigationMenu.List
                  className={classNames("flex flex-col", {
                    "items-center": minimized,
                  })}
                >
                  {menuList.map((menuItems) => (
                    <RadixNavigationMenu.Item key={menuItems.key}>
                      <menuItems.component
                        minimized={minimized}
                        setMobileMenuOpen={setMobileMenuOpen}
                      />
                    </RadixNavigationMenu.Item>
                  ))}
                </RadixNavigationMenu.List>
              </RadixNavigationMenu.Root>
            </div>
          </div>
          <div>
            {extraMenu.length > 0 && (
              <RadixNavigationMenu.Root>
                <RadixNavigationMenu.List
                  className={classNames("flex flex-col", {
                    "items-center": minimized,
                  })}
                >
                  {extraMenu.map((menuItems) => (
                    <RadixNavigationMenu.Item
                      key={menuItems.key}
                      className="w-full"
                    >
                      <menuItems.component
                        minimized={minimized}
                        setMobileMenuOpen={setMobileMenuOpen}
                      />
                    </RadixNavigationMenu.Item>
                  ))}
                </RadixNavigationMenu.List>
              </RadixNavigationMenu.Root>
            )}
            <div className="hidden md:flex w-full justify-end">
              <IconButton
                size="medium"
                customIcon={
                  minimized ? <ChevronsRightSolid /> : <ChevronsLeftSolid />
                }
                onClick={() => setStoredMinimizedValue((!minimized).toString())}
              />
            </div>
          </div>
        </div>
        <main
          className={classNames(
            "lg:ml-4 bg-white flex flex-grow flex-col h-full overflow-x-auto rounded-lg",
          )}
        >
          <div className="inline md:hidden">
            <Dialog.Root open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50" />
                <Dialog.Content
                  className={classNames(
                    "fixed top-0 h-full w-2/3 max-w-[350px] overflow-auto p-2 flex flex-col justify-between",
                    "bg-primary-600 text-white",
                    {
                      "-left-52": !mobileMenuOpen,
                      "left-0": mobileMenuOpen,
                    },
                  )}
                >
                  <div>
                    <RadixNavigationMenu.Root>
                      <RadixNavigationMenu.List className={"flex flex-col"}>
                        {menuList.map((menuItems) => (
                          <RadixNavigationMenu.Item key={menuItems.key}>
                            <menuItems.component
                              minimized={false}
                              setMobileMenuOpen={setMobileMenuOpen}
                            />
                          </RadixNavigationMenu.Item>
                        ))}
                      </RadixNavigationMenu.List>
                    </RadixNavigationMenu.Root>
                  </div>
                  <div>
                    {extraMenu.length > 0 && (
                      <RadixNavigationMenu.Root>
                        <RadixNavigationMenu.List
                          className={classNames("flex flex-col", {
                            "items-center": minimized,
                          })}
                        >
                          {extraMenu.map((menuItems) => (
                            <RadixNavigationMenu.Item
                              key={menuItems.key}
                              className="w-full"
                            >
                              <menuItems.component
                                minimized={minimized}
                                setMobileMenuOpen={setMobileMenuOpen}
                              />
                            </RadixNavigationMenu.Item>
                          ))}
                        </RadixNavigationMenu.List>
                      </RadixNavigationMenu.Root>
                    )}
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          </div>
          <div
            className={classNames(
              "flex md:hidden flex-initial",
              "justify-between items-center w-full min-h-11 py-4 px-2 border-b bg-primary-50",
            )}
          >
            <button
              className="w-8 h-8 rounded text-primary-700"
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
              }}
            >
              <BarsRegular />
            </button>
          </div>
          <div className="m-2 flex-auto overflow-y-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </>
  );
}

export default GeneralLayout;
